<template>
  <div class="resource-document">
    <UkaskoLoader :is-loading="!isReady">
      <div class="resource-document__top">
        <AppHeader
          class="resource-document__header"
          :animation-enabled="false"
          :is-mobile-menu-open="isMobileMenuOpen"
          @header-resize="closeMobileMenuOnResize"
        />
        <DocumentHero :document="document" :page="page" />
      </div>
      <div class="resource-document__content">
        <div class="resource-document__body">
          <DocumentSummary :page="page" />
          <DocumentSample :page="page" :variant="document.sampleVariant" />
          <DocumentContents :page="page" />
          <DocumentAudience :page="page" />
          <DocumentCta :page="page" :slug="document.slug" />
          <DocumentDetails :page="page" />
        </div>
      </div>
      <div class="resource-document__footer-edge"></div>
      <AppFooter />
    </UkaskoLoader>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getResourceDocumentTextPage, RESOURCES_HUB_TEXT_PAGE } from '../../config';
import { extractUtmParams } from '../../lib';
import DocumentAudience from './document-audience';
import DocumentContents from './document-contents';
import DocumentCta from './document-cta';
import DocumentDetails from './document-details';
import DocumentHero from './document-hero';
import DocumentSample from './document-sample';
import DocumentSummary from './document-summary';
import { UkaskoLoader } from '@/shared/ui/ukasko-loader';
import { AppFooter, AppHeader, promoMobileMenu } from '@/widgets/promo';

const PROMO_TEXT_PAGE = 'promo';

export default {
  mixins: [ promoMobileMenu ],
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  components: {
    AppHeader, AppFooter, UkaskoLoader, DocumentHero, DocumentSummary, DocumentSample, DocumentContents,
    DocumentAudience, DocumentCta, DocumentDetails,
  },
  computed: {
    ...mapGetters('textBlocksModule', ['isTextBlocksReady']),
    page() {
      return getResourceDocumentTextPage(this.document.slug);
    },
    isReady() {
      return this.isTextBlocksReady(PROMO_TEXT_PAGE)
        && this.isTextBlocksReady(RESOURCES_HUB_TEXT_PAGE)
        && this.isTextBlocksReady(this.page);
    },
  },
  watch: {
    document: {
      immediate: true,
      handler(document) {
        this.fetchTextBlocks(getResourceDocumentTextPage(document.slug));
        this.startSelection([document.slug]);
      },
    },
  },
  created() {
    if (!this.isTextBlocksReady(PROMO_TEXT_PAGE)) {
      this.fetchTextBlocks(PROMO_TEXT_PAGE);
    }
    if (!this.isTextBlocksReady(RESOURCES_HUB_TEXT_PAGE)) {
      this.fetchTextBlocks(RESOURCES_HUB_TEXT_PAGE);
    }
    this.rememberUtm(extractUtmParams(this.$route.query));
  },
  methods: {
    ...mapActions('textBlocksModule', ['fetchTextBlocks']),
    ...mapActions('agentResourcesModule', ['rememberUtm', 'startSelection']),
  },
};
</script>

<style lang="scss" scoped>
.resource-document {
  font-family: 'Geist', sans-serif;
  color: #373A3D;
  background-color: #FCFDFF;

  &__top {
    position: relative;
    background-color: #CADDFC;
    padding-bottom: 96px;

    @media (max-width: 800px) {
      padding-bottom: 64px;
    }
  }

  &__header {
    position: relative;
    z-index: 2;
  }

  &__content {
    position: relative;
    margin-top: -48px;
    padding: 48px 0 96px;
    background-color: #FCFDFF;
    border-radius: 48px 48px 0 0;

    @media (max-width: 800px) {
      margin-top: -32px;
      padding: 32px 0 64px;
      border-radius: 32px 32px 0 0;
    }
  }

  &__body {
    padding: 48px 24px 0;
    display: flex;
    flex-direction: column;
    gap: 120px;

    @media (max-width: 800px) {
      padding: 24px 16px 0;
      gap: 64px;
    }
  }

  &__footer-edge {
    height: 32px;
    background-color: #313437;
    border-radius: 32px 32px 0 0;
  }
}
</style>
